import type React from "react"

import { useEffect, useRef, useState } from "react"

/**
 * Props for the useOtpInput hook.
 */
interface UseOtpInputProps {
  /**
   * The number of digit fields to manage.
   */
  readonly length: number

  /**
   * Whether to focus the first input field on mount.
   */
  readonly autoFocus: boolean

  /**
   * Whether the inputs are disabled.
   */
  readonly isDisabled: boolean | undefined

  /**
   * Callback fired whenever the OTP value changes.
   */
  readonly onChange: ((code: string) => void) | undefined

  /**
   * Callback fired when all digit fields are filled.
   */
  readonly onComplete: ((code: string) => void) | undefined
}

/**
 * Manages OTP input state, focus management, and keyboard interactions.
 * @param {UseOtpInputProps} props - The configuration for the OTP input behavior.
 * @returns State values and event handlers for the OTP input fields.
 */
export const useOtpInput = ({ length, autoFocus, isDisabled, onChange, onComplete }: UseOtpInputProps) => {
  const [values, setValues] = useState<string[]>(Array(length).fill(""))
  const inputRefsRef = useRef<(HTMLInputElement | null)[]>([])

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus && !isDisabled && inputRefsRef.current[0]) {
      inputRefsRef.current[0].focus()
    }
  }, [autoFocus, isDisabled])

  const fireCallbacks = (newValues: string[]): void => {
    const code = newValues.join("")
    onChange?.(code)
    if (code.length === length) {
      onComplete?.(code)
    }
  }

  const handleChange = (index: number, value: string): void => {
    if (isDisabled) return

    // Handle browser autofill or paste (multiple characters)
    if (value.length > 1) {
      const digits = value.replace(/\D/g, "").slice(0, length)
      const newValues = [...values]
      for (let i = 0; i < digits.length && index + i < length; i++) {
        newValues[index + i] = digits[i]
      }
      setValues(newValues)
      fireCallbacks(newValues)

      // Focus last filled input or next empty one
      const lastFilledIndex = Math.min(index + digits.length - 1, length - 1)
      const nextEmptyIndex = newValues.findIndex((v, i) => i > lastFilledIndex && v === "")
      const focusIndex = nextEmptyIndex !== -1 ? nextEmptyIndex : lastFilledIndex
      inputRefsRef.current[focusIndex]?.focus()
      return
    }

    // Handle single digit input
    const digit = value.replace(/\D/g, "").slice(-1)
    const newValues = [...values]
    newValues[index] = digit
    setValues(newValues)
    fireCallbacks(newValues)

    // Auto-advance to next input
    if (digit && index < length - 1) {
      inputRefsRef.current[index + 1]?.focus()
    }
  }

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (isDisabled) return

    if (event.key === "Backspace") {
      event.preventDefault()
      const newValues = [...values]
      if (values[index]) {
        // Clear current field
        newValues[index] = ""
        setValues(newValues)
        fireCallbacks(newValues)
      } else if (index > 0) {
        // Move to previous field and clear it
        newValues[index - 1] = ""
        setValues(newValues)
        fireCallbacks(newValues)
        inputRefsRef.current[index - 1]?.focus()
      }
    } else if (event.key === "ArrowLeft" && index > 0) {
      event.preventDefault()
      inputRefsRef.current[index - 1]?.focus()
    } else if (event.key === "ArrowRight" && index < length - 1) {
      event.preventDefault()
      inputRefsRef.current[index + 1]?.focus()
    }
  }

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>): void => {
    if (isDisabled) return

    event.preventDefault()
    const pastedData = event.clipboardData.getData("text")
    const digits = pastedData.replace(/\D/g, "").slice(0, length)

    if (digits) {
      const newValues = digits.split("").concat(Array(length - digits.length).fill(""))
      setValues(newValues)
      fireCallbacks(newValues)

      // Focus last filled input
      const lastFilledIndex = Math.min(digits.length - 1, length - 1)
      inputRefsRef.current[lastFilledIndex]?.focus()
    }
  }

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>): void => {
    if (isDisabled) return
    event.target.select()
  }

  return {
    values,
    inputRefsRef,
    handleChange,
    handleKeyDown,
    handlePaste,
    handleFocus,
  }
}
